import i18next, { type i18n as I18n, type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import {
  SOURCE_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type CmsLanguage,
} from "./languages";

/** Namespaces every page needs; the rest load when a page first asks. */
export const CORE_NAMESPACES = [
  "common",
  "glossary",
  "errors",
  "shell",
] as const;

/**
 * The CMS's one i18next instance. Never import it in a component: use
 * `useTranslation()`, which re-renders on a language change.
 */
export const i18n: I18n = i18next.createInstance();

function capitalize(value: unknown, lng: string | undefined): string {
  const s = String(value ?? "");
  return s.charAt(0).toLocaleUpperCase(lng) + s.slice(1);
}

function baseOptions(language: CmsLanguage) {
  return {
    lng: language,
    fallbackLng: SOURCE_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    ns: [...CORE_NAMESPACES],
    defaultNS: "common",
    fallbackNS: false as const,
    // React escapes; double escaping would show &amp; in the UI.
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { bindI18n: "languageChanged loaded" },
  };
}

function registerFormatters(instance: I18n): void {
  instance.services.formatter?.add("capitalize", capitalize);
}

/** Loads each namespace's JSON on first use, per language. */
export async function initI18n(language: CmsLanguage): Promise<I18n> {
  if (i18n.isInitialized) return i18n;
  await i18n
    .use(
      resourcesToBackend(
        (lng: string, ns: string) => import(`./locales/${lng}/${ns}.json`)
      )
    )
    .use(initReactI18next)
    .init(baseOptions(language));
  registerFormatters(i18n);
  return i18n;
}

/** Synchronous init with bundled resources, for tests. */
export function initI18nWithResources(
  resources: Resource,
  language: CmsLanguage = SOURCE_LANGUAGE
): I18n {
  if (!i18n.isInitialized) {
    void i18n
      .use(initReactI18next)
      .init({ ...baseOptions(language), resources, initAsync: false });
    registerFormatters(i18n);
  }
  return i18n;
}
